import React, { useEffect, useState } from 'react';
import { getLocation, getStatus, submitCheckin } from '_services/remote-checkin-service';
import FlightPickerForm from './components/FlightPickerForm';
import StatusView from './components/StatusView';
import logo from '_assets/images/PRM-Logo-PNG/PRM-Assist-Logo-Col.png';
import './remotecheckin.css';

// Extract locationCode from path: /remotecheckin/DUB-10 → "DUB-10"
const getLocationCode = () => {
    const parts = window.location.pathname.split('/');
    const idx = parts.findIndex(p => p === 'remotecheckin');
    return idx !== -1 ? parts[idx + 1] : null;
};

const RemoteCheckinPage = () => {
    const locationCode = getLocationCode();

    const [location, setLocation]           = useState(null);
    const [locationError, setLocationError] = useState('');
    const [loading, setLoading]             = useState(true);

    // Check-in state — set after submission or when an active session cookie is found
    const [taskState, setTaskState] = useState(null);
    const [cancelled, setCancelled] = useState(false);

    // Form state
    const [submitting, setSubmitting]   = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (!locationCode) {
            setLocationError('Invalid check-in link. Please re-scan the QR code.');
            setLoading(false);
            return;
        }

        const init = async () => {
            try {
                const loc = await getLocation(locationCode);
                setLocation(loc);

                // Resume status view if the session cookie is still active
                const status = await getStatus();
                if (status) setTaskState(status.state);
            } catch {
                setLocationError("We couldn't load this check-in page. Please re-scan the QR code.");
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [locationCode]);

    const handleSubmit = async (firstName, lastName, rawFlightNumber, departureDate, flightType, assistance) => {
        setSubmitting(true);
        setSubmitError('');
        try {
            const result = await submitCheckin(locationCode, firstName, lastName, rawFlightNumber, departureDate, flightType, assistance);
            setTaskState(result.state);
        } catch (e) {
            // Use the server's message when available (e.g. booking-not-found 404);
            // fall back to a generic network error message.
            const isServerMsg = e.message && !e.message.startsWith('Request failed');
            setSubmitError(
                isServerMsg
                    ? e.message
                    : "We couldn't submit your request. Please check your connection and try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelled = () => {
        setCancelled(true);
        setTaskState(null);
    };

    if (loading) {
        return (
            <div className="rc-page">
                <div className="rc-card" style={{ textAlign: 'center', paddingTop: 48 }}>
                    <p style={{ color: '#6c757d' }}>Loading…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rc-page">
            <header className="rc-header">
                <img src={logo} alt="PRM Assist" className="rc-header-logo" />
                {location && (
                    <div className="rc-header-location-row">
                        <p className="rc-header-at-label">Location:</p>
                        <p className="rc-header-location">{location.title}</p>
                    </div>
                )}
            </header>

            <main>
                <div className="rc-card">
                    {locationError ? (
                        <div>
                            <p className="rc-title" style={{ color: '#dc3545' }}>Something went wrong</p>
                            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>{locationError}</p>
                        </div>
                    ) : cancelled ? (
                        <div style={{ textAlign: 'center' }}>
                            <p className="rc-title">Request cancelled</p>
                            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                                Your assistance request has been cancelled.
                            </p>
                        </div>
                    ) : taskState ? (
                        <StatusView
                            initialState={taskState}
                            onCancelled={handleCancelled}
                        />
                    ) : (
                        location && (
                            <FlightPickerForm
                                location={location}
                                onSubmit={handleSubmit}
                                submitting={submitting}
                                submitError={submitError}
                            />
                        )
                    )}
                </div>

                {/* App download branding */}
                <div className="rc-app-banner">
                    <p className="rc-app-banner-sub">
                        Download the <strong>PRM Assist</strong> app.
                    </p>
                    <div className="rc-app-badge-row">
                        <a
                            href="https://apps.apple.com/ie/app/prm-assist/id1574371912"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rc-app-badge"
                            aria-label="Download on the App Store"
                        >
                            <span className="rc-app-badge-icon">&#xf8ff;</span>
                            <span>
                                <span className="rc-app-badge-sub">Download on the</span>
                                <span className="rc-app-badge-store">App Store</span>
                            </span>
                        </a>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.prmassist"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rc-app-badge"
                            aria-label="Get it on Google Play"
                        >
                            <span className="rc-app-badge-icon rc-app-badge-icon--android">▶</span>
                            <span>
                                <span className="rc-app-badge-sub">Get it on</span>
                                <span className="rc-app-badge-store">Google Play</span>
                            </span>
                        </a>
                    </div>
                    <p className="rc-app-banner-footer">© {new Date().getFullYear()} PRM Assist. All rights reserved.</p>
                </div>
            </main>
        </div>
    );
};

export default RemoteCheckinPage;
